
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useModalA11y } from '../../hooks/useModalA11y';
import './CommentsSheet.css';
import { Post, Comment, useApp } from '../../context/AppContext';
import { commentsAPI } from '../../services/api';

interface CommentsSheetProps {
  post: Post;
  onClose: () => void;
}

const CommentsSheet: React.FC<CommentsSheetProps> = ({ post, onClose }) => {
  const { currentUser, addComment, likeComment, getComments, isAdminView, adminUser } = useApp();
  const [newComment, setNewComment] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [apiComments, setApiComments] = useState<Comment[] | null>(null);
  const [brokenAvatars, setBrokenAvatars] = useState<Record<string, boolean>>({});
  // Swipe-down-to-dismiss: tracked in pixels of drag offset. When the
  // user releases past SWIPE_CLOSE_THRESHOLD, the sheet animates out.
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartYRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dialogRef = useModalA11y({ onEscape: onClose });
  const SWIPE_CLOSE_THRESHOLD = 140;
  const markAvatarBroken = (url: string) => {
    if (!url) return;
    setBrokenAvatars(prev => prev[url] ? prev : { ...prev, [url]: true });
  };
  const initialOf = (s: string | undefined) => (s || '?').trim().charAt(0).toUpperCase() || '?';

  const localComments = getComments(post.id);

  // Merge: API comments as base, local comments override (preserves likes)
  const comments: Comment[] = (() => {
    if (!apiComments) return localComments;
    const localById = new Map(localComments.map(c => [c.id, c]));
    const merged = apiComments.map((ac: any) => {
      const local = localById.get(ac.id);
      return local ?? {
        id: ac.id,
        postId: post.id,
        userId: ac.user_id,
        username: ac.display_name || ac.username,
        userAvatar: ac.avatar || '',
        content: ac.content,
        timestamp: new Date(ac.created_at),
        likes: 0,
        likedBy: [],
      };
    });
    // Append local-only comments (not yet in DB)
    localComments.forEach(lc => {
      if (!apiComments.find((ac: any) => ac.id === lc.id)) merged.push(lc);
    });
    return merged;
  })();

  // Fetch DB comments on open
  useEffect(() => {
    commentsAPI.getForPost(post.id)
      .then(res => { if (res?.comments) setApiComments(res.comments as any[]); })
      .catch(() => {});
  }, [post.id]);
  const user = isAdminView ? adminUser : currentUser;

  useEffect(() => {
    // Focus input on open
    setTimeout(() => inputRef.current?.focus(), 300);
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Swipe-down-to-dismiss. We only initiate the drag if the touch
  // starts on the header (handle area) — touches that begin inside the
  // scrolling comments list shouldn't hijack a list scroll.
  const handleHeaderTouchStart = (e: React.TouchEvent) => {
    dragStartYRef.current = e.touches[0].clientY;
  };
  const handleSheetTouchMove = (e: React.TouchEvent) => {
    if (dragStartYRef.current == null) return;
    const dy = e.touches[0].clientY - dragStartYRef.current;
    setDragOffset(dy > 0 ? dy : 0);
  };
  const handleSheetTouchEnd = () => {
    if (dragStartYRef.current == null) return;
    dragStartYRef.current = null;
    if (dragOffset >= SWIPE_CLOSE_THRESHOLD) {
      setDragOffset(0);
      handleClose();
    } else {
      setDragOffset(0);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      addComment(post.id, newComment.trim());
      setNewComment('');
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return `${Math.floor(days / 7)}w`;
  };

  // Portal to body — same containing-block reason as the other overlays.
  return createPortal((
    <div className={`comments-backdrop ${isClosing ? 'closing' : ''}`} onClick={handleBackdropClick} ref={dialogRef} role="dialog" aria-modal="true" aria-label="Comments" tabIndex={-1}>
      <div
        ref={sheetRef}
        className={`comments-sheet ${isClosing ? 'closing' : ''}`}
        style={dragOffset > 0 ? { transform: `translateY(${dragOffset}px)`, transition: 'none' } : undefined}
        onTouchMove={handleSheetTouchMove}
        onTouchEnd={handleSheetTouchEnd}
      >
        <div
          className="comments-header"
          onTouchStart={handleHeaderTouchStart}
        >
          <div className="comments-handle" />
          <h3>Comments</h3>
          <span className="comments-count">{comments.length}</span>
          <button className="comments-close" onClick={handleClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="comments-list">
          {comments.length === 0 ? (
            <div className="comments-empty">
              <div className="empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
                </svg>
              </div>
              <p>No comments yet</p>
              <span>Be the first to comment!</span>
            </div>
          ) : (
            comments.map(comment => (
              <div key={comment.id} className="comment-item">
                {comment.userAvatar && !brokenAvatars[comment.userAvatar]
                  ? <img
                      src={comment.userAvatar}
                      alt={comment.username}
                      className="comment-avatar"
                      onError={() => markAvatarBroken(comment.userAvatar)}
                    />
                  : <span className="comment-avatar comment-avatar--initial" aria-hidden="true">{initialOf(comment.username)}</span>}
                <div className="comment-body">
                  <div className="comment-content">
                    <span className="comment-username">{comment.username}</span>
                    <span className="comment-text">{comment.content}</span>
                  </div>
                  <div className="comment-meta">
                    <span className="comment-time">{formatTimeAgo(comment.timestamp)}</span>
                    {comment.likes > 0 && (
                      <span className="comment-likes">{comment.likes} likes</span>
                    )}
                    {/* Reply button removed 2026-05-17 — the backend has
                        no parent_comment_id wiring yet, so the prior
                        <button>Reply</button> was a dead noop that
                        confused users. Reintroduce once threading lands. */}
                  </div>
                </div>
                <button
                  className={`comment-like-btn ${comment.likedBy.includes(user.id) ? 'liked' : ''}`}
                  onClick={() => likeComment(post.id, comment.id)}
                >
                  <svg viewBox="0 0 24 24" fill={comment.likedBy.includes(user.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>

        <form className="comments-input-area" onSubmit={handleSubmit}>
          {user.avatar && !brokenAvatars[user.avatar]
            ? <img
                src={user.avatar}
                alt={user.displayName}
                className="input-avatar"
                onError={() => markAvatarBroken(user.avatar)}
              />
            : <span className="input-avatar input-avatar--initial" aria-hidden="true">{initialOf(user.displayName)}</span>}
          <input
            ref={inputRef}
            type="text"
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <button type="submit" className={`send-btn ${newComment.trim() ? 'active' : ''}`} disabled={!newComment.trim()}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </form>
      </div>
    </div>
  ), document.body);
};

export default CommentsSheet;
